// @LTG-ID: F-060
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: 07_PAGES_AND_ROUTES.md, 08_FEATURES.md#F-060,
//        source `Refund Policy.html` lines 200-298 (body content + disclaimer).
// @CHANGED: 2026-02 session_004 (CHG-0057)
// @OWNER: web
//
// F-060 — `/refund-policy` route. Verbatim editorial port of source
// `/app/source_reference/Refund Policy.html` lines 200-298. Source's chrome
// (lines 41-71 + lines 202-207 header + lines 302-311 footer) is intentionally
// NOT ported — SiteLayout supplies Header, ScrollProgressBar, Footer,
// ScrollTopFAB, ScrollToTop. The page renders inside `<main><Outlet />`.
//
// Note on links: the email + phone in the disclaimer-block + the nccpaboard.gov
// link in Section 07 are NOT `app.letstalkgrowth.com` anchors, so per ADR-008 they stay
// as bare `<a>` tags. ADR-008's no-bare-anchor rule scope is `app.letstalkgrowth.com` only.
//
// The page intentionally has NO `<ExternalCTA>` inside the body — Refund Policy
// is a legal document, not a marketing surface. A `refund-policy-order` cta-id
// is pre-reserved in `lib/cta-ids.js` for future use if P6 adds an in-page CTA,
// but no anchor uses it today.

import { useEffect } from "react";
import BubblePeriod from "../components/brand/BubblePeriod";
import "./refund-policy.css";

export const RefundPolicy = () => {
    useEffect(() => {
        document.title =
            "Refund Policy · Let's Talk Growth by KGOB CPAs & Advisors";
    }, []);

    return (
        <div className="ltg-refund-page" data-testid="refund-policy-page">
            <main className="ltg-refund-main">
                <div
                    className="ltg-refund-doc-meta"
                    data-testid="refund-doc-meta"
                >
                    <span>Legal</span>
                    <span>Refund Policy</span>
                    <span>Let's Talk Growth</span>
                    <span>KGOB CPAs &amp; Advisors</span>
                </div>

                <h1
                    className="ltg-refund-h1"
                    data-testid="refund-policy-h1"
                >
                    Refund Policy<BubblePeriod />
                </h1>

                <p
                    className="ltg-refund-lede"
                    data-testid="refund-policy-lede"
                >
                    Let's Talk Growth (&ldquo;we,&rdquo; &ldquo;us&rdquo;) is
                    operated by KGOB CPAs &amp; Advisors PLLC, a North
                    Carolina&ndash;registered CPA firm. We stand behind the
                    technical quality of our deliverables and offer the
                    following refund policy.
                </p>

                {/* Section 01 — Strategic Snapshot $995 */}
                <h2
                    className="ltg-refund-h2"
                    data-testid="refund-section-01"
                >
                    <span className="ltg-refund-h2__num">01</span>
                    Strategic Snapshot, $995 one-time.
                </h2>
                <p>
                    You may request a full refund within fourteen (14)
                    calendar days of the timestamp on the email by which we
                    deliver your PDF report, provided that one of the
                    following applies.
                </p>
                <ol type="a">
                    <li>
                        The report PDF or interactive dashboard was not
                        delivered, or could not be accessed, due to a
                        technical defect on our side that we did not cure
                        within five (5) business days of your written notice.
                    </li>
                    <li>
                        The report contains a material computational error
                        that we did not cure within five (5) business days of
                        your written notice.
                    </li>
                    <li>You were charged in duplicate.</li>
                    <li>
                        You purchased in error and have not opened the PDF or
                        logged into the dashboard.
                    </li>
                </ol>
                <p>
                    <strong>Refunds are not available</strong> for
                    disagreement with the indicative valuation,
                    dissatisfaction with the methodology, changes in your
                    business after the inputs you supplied, or failure of the
                    report to produce a particular financial outcome. The
                    Strategic Snapshot is an indicative valuation, not a
                    formal business appraisal under AICPA Statement on
                    Standards for Valuation Services (SSVS) No.&nbsp;1 or
                    USPAP.
                </p>

                {/* Section 02 — Growth Membership (monthly recurring) */}
                <h2
                    className="ltg-refund-h2"
                    data-testid="refund-section-02"
                >
                    <span className="ltg-refund-h2__num">02</span>
                    Growth Membership, $95 per month.
                </h2>
                <p>
                    The Growth Membership is available by invitation to
                    customers who have purchased a Strategic Snapshot. You
                    may cancel at any time through your account dashboard
                    (<em>
                        Settings &rarr; Subscription &rarr; Cancel
                    </em>). Cancellation takes effect at the end of the
                    current billing month; we do not pro-rate. We do not
                    refund prior months. If you were charged after canceling
                    because of a system error on our side, we will refund the
                    erroneous charge upon written notice within thirty (30)
                    days of the charge.
                </p>

                {/* Section 03 — How to request */}
                <h2
                    className="ltg-refund-h2"
                    data-testid="refund-section-03"
                >
                    <span className="ltg-refund-h2__num">03</span>How to
                    request a refund.
                </h2>

                <div
                    className="ltg-refund-callout"
                    data-testid="refund-request-callout"
                >
                    <strong>
                        Reply to your delivery confirmation email
                    </strong>
                    Place{" "}
                    <span className="ltg-refund-callout__kbd">REFUND</span>{" "}
                    in the subject line, with a one-sentence description of
                    which ground in Section&nbsp;01 above applies. We respond
                    within three (3) business days. This is the only channel
                    through which we can process refund requests.
                </div>

                <p>
                    We cannot process refund requests submitted through chat,
                    phone, third-party platforms, or social media. Approved
                    refunds process to your original payment method within
                    five (5) business days of approval.
                </p>

                {/* Section 04 — Chargeback */}
                <h2
                    className="ltg-refund-h2"
                    data-testid="refund-section-04"
                >
                    <span className="ltg-refund-h2__num">04</span>Chargeback
                    policy.
                </h2>
                <p>
                    Eligibility under this refund policy is conditioned on
                    first requesting a refund through the channel in
                    Section&nbsp;03 above.{" "}
                    <strong>
                        If you initiate a credit-card dispute or chargeback
                        before submitting a refund request through
                        Section&nbsp;03, you forfeit eligibility under this
                        policy.
                    </strong>{" "}
                    We will respond to your card issuer with evidence of
                    delivery, our terms, your acceptance record, and this
                    policy.
                </p>
                <p>
                    This does not waive any rights you have under applicable
                    law.
                </p>

                {/* Section 05 — Indicative valuation disclaimer */}
                <h2
                    className="ltg-refund-h2"
                    data-testid="refund-section-05"
                >
                    <span className="ltg-refund-h2__num">05</span>Indicative
                    valuation disclaimer.
                </h2>
                <p>
                    Our deliverables provide an{" "}
                    <strong>indicative valuation</strong> based on data you
                    supply, generated through a productized methodology
                    developed by KGOB CPAs &amp; Advisors PLLC.
                </p>
                <p>
                    The output is <strong>not</strong> a valuation engagement
                    or calculation engagement under AICPA Statement on
                    Standards for Valuation Services (SSVS) No.&nbsp;1,{" "}
                    <strong>not</strong> a USPAP-compliant business
                    appraisal, and <strong>not</strong> an opinion of value.
                    Under SSVS No.&nbsp;1 &para;9, the deliverable falls
                    within the standard's exclusion for mechanical
                    computations that do not rise to the level of an
                    engagement to estimate value. Had a formal valuation
                    engagement been performed, the results may have been
                    materially different.
                </p>
                <p>
                    Do not use our report as the sole basis for any sale,
                    financing, tax filing, litigation, or other transaction.
                    For such purposes, retain a credentialed business
                    appraiser (ASA, ABV, CVA, CBA).
                </p>

                {/* Section 06 — Governing law */}
                <h2
                    className="ltg-refund-h2"
                    data-testid="refund-section-06"
                >
                    <span className="ltg-refund-h2__num">06</span>Governing
                    law and venue.
                </h2>
                <p>
                    This policy is governed by the laws of the State of North
                    Carolina. Disputes shall be brought in the state or
                    federal courts located in Mecklenburg County, North
                    Carolina, except that either party may bring an
                    individual action in any small-claims court of competent
                    jurisdiction.{" "}
                    <strong>
                        You and we each waive any right to a jury trial and
                        any right to participate in a class action.
                    </strong>
                </p>

                {/* Section 07 — Professional conduct complaints */}
                <h2
                    className="ltg-refund-h2"
                    data-testid="refund-section-07"
                >
                    <span className="ltg-refund-h2__num">07</span>Professional
                    conduct complaints.
                </h2>
                <p>
                    KGOB CPAs &amp; Advisors PLLC is registered with the
                    North Carolina State Board of Certified Public Accountant
                    Examiners. Complaints about the professional conduct of
                    our CPAs may be filed at{" "}
                    <a
                        href="https://nccpaboard.gov"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="refund-nccpaboard-link"
                    >
                        nccpaboard.gov
                    </a>
                    .
                </p>

                {/* Closing disclaimer block */}
                <div
                    className="ltg-refund-disclaimer-block"
                    data-testid="refund-disclaimer-block"
                >
                    <p>
                        Effective May&nbsp;23, 2026 &middot; Version&nbsp;1.0.
                        KGOB CPAs &amp; Advisors PLLC (Kohari Gonzalez Oneyear
                        &amp; Brown PLLC), 2740 East W.T. Harris Blvd, Suite
                        200, Charlotte, NC 28213. Contact:{" "}
                        <a
                            href="mailto:support@letstalkgrowth.com"
                            data-testid="refund-contact-email"
                        >
                            support@letstalkgrowth.com
                        </a>{" "}
                        &middot; (704)&nbsp;599-3355.
                    </p>
                    <p>
                        This policy is published as a marketing summary of
                        our refund terms. The binding contract is the
                        click-through Terms of Service and Refund Policy
                        accepted at checkout on{" "}
                        <a
                            href="https://app.letstalkgrowth.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="refund-app-domain-link"
                        >
                            app.letstalkgrowth.com
                        </a>
                        . If the two
                        documents conflict, the click-through version
                        controls.
                    </p>
                </div>
            </main>
        </div>
    );
};

export default RefundPolicy;
